import { useEffect, useState } from "react";
import { Input, Modal, ModalBody } from "reactstrap";
import { cleanRut, formatRut, validateRut } from "rutlib/lib";
import { useRecoilValue } from "recoil";
import { toast } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

import { useClientsActions } from "../../../../services/actions/clients.actions";
import { crudClientAtom } from "../../../../services/atoms/clients.atoms";
import { ButtonLoading } from "../../../../components/buttons/button-loading";
import { newValidatePhone, validateEmail} from "../../../../helpers/functions";
import UtilsAPI from "../../../../services/api/utils.api";
import ClientAPI from "../../../../services/api/clients.api";
import { featureToggles } from "../../../../utils/featureToggles";

const initialDataStates = {
  id: "",
  rut: "",
  email: "",
  phone: "",
  sector: "",
  name: "",
  responsibleName: "",
  responsibleEmail: "",
  responsiblePhoneNumber: "",
  responsiblePosition: "",
  responsibleUserId: null,
  isAdmin: "true",
  isUserBasic: "false",
  groupId: "",
  groupName: "",
  externalSelection: false,
  internalSelection: false,
  testsAvailable: "",
  assignedTest: [],
};

const testData = ["Test DISC + motivadores"];
const TEXT_WARNING="El nombre de empresa no puede ser editado una vez creado el cliente, revisa que se encuentre bien redactado."
const TEXT_ERROR_EMPRESA = "Este nombre ya se encuentra registrado, por favor, intenta con otro."
const TEXT_ERROR_EMAIL = "Este email ya se encuentra registrado, por favor, intenta con otro."


const CreateClientModal = (props) => {
  const { open, toggle, pageHandler, isCreate, dataEdit, dataCheck} = props;
  const { createClient, clientsList, resetClientCrud, editClient } = useClientsActions();
  const [data, setData] = useState(initialDataStates);
  const client = useRecoilValue(crudClientAtom);
  const [steps, setSteps] = useState(1);
  const [planExternal, setPlanExternal] = useState(false);
  const [planInternal, setPlanInternal] = useState(false);
  const [testSelected, setTestSelected] = useState(false);
  const [sectorList, setSectorList] = useState([]);
  const [nameRepeated, setNameRepeated]= useState(false)
  const [emailRepeated, setEmailRepeated] = useState(false)
  const [clientUsers, setClientUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const handleChange = (key, value) =>
    setData((prevValues) => ({ ...prevValues, [key]: value }));

  const handleCancelForm = () => {
    if (isCreate) {
      setData(initialDataStates);
      setPlanExternal(false);
      setPlanInternal(false);
      setTestSelected(false);
      setSteps(1)
    }
  };

  const handleValidateNotRepeated = (key, value)=>{
    if(key==='name'){
      const nameExist = dataCheck?.find((elements)=>elements.name.toLowerCase() === value.toLowerCase())
      setNameRepeated(nameExist)
    }
    if(key==='email'){
      const emailExist = dataCheck?.find((element)=>element.responsibleEmail===value)
      setEmailRepeated(emailExist)
    }
  }

  const handleSelectUser = (userId) => {
    const user = clientUsers.find(u => u.id === userId);
    if (!user) {
      setData(prev => ({ ...prev, responsibleUserId: null, responsibleName: "", email: "", phone: "" }));
      return;
    }
    setData(prev => ({
      ...prev,
      responsibleUserId: user.id,
      responsibleName: user.name,
      email: user.userEmail,
      phone: user.usersPhone || "",
    }));
  };

  useEffect(() => {
    client.error && toast.error(client.message);
    return () => resetClientCrud();
    // eslint-disable-next-line
  }, [client.error, client.message]);

  useEffect(() => {
    if (!isCreate) {
      setData({
        ...dataEdit,
        phone: dataEdit.responsiblePhoneNumber,
        email: dataEdit.responsibleEmail,
        planExternal: dataEdit.externalSelection,
        planInternal: dataEdit.internalSelection,
        // El campo edita el saldo absoluto de cortesía, no el total mostrado
        // (compradas + cortesía). El detalle (findClient) trae `courtesyTestsAvailable`;
        // la fila de la lista (SELECT *) solo trae la columna cruda `testsAvailable`, que
        // es justamente el saldo de cortesía. Se usa el primero y, si no está, el segundo.
        testsAvailable: dataEdit.courtesyTestsAvailable ?? dataEdit.testsAvailable ?? 0,
        assignedTest: dataEdit.assignedTest ?? []
      });
      setPlanExternal(dataEdit?.externalSelection)
      setPlanInternal(dataEdit?.internalSelection)
      setTestSelected(dataEdit?.assignedTest?.length !== 0 ? true : false)

      const loadUsers = async () => {
        setLoadingUsers(true);
        try {
          const res = await ClientAPI.getClientUsers(dataEdit.id);
          if (res?.data) {
            setClientUsers(res.data);
            if (!dataEdit.responsibleUserId) {
              const match = res.data.find(u => u.userEmail === dataEdit.responsibleEmail);
              if (match) {
                setData(prev => ({ ...prev, responsibleUserId: match.id }));
              }
            }
          }
        } catch {
          setClientUsers([]);
        } finally {
          setLoadingUsers(false);
        }
      };
      loadUsers();
    }
  }, [dataEdit?.id]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isCreate) {
      const idForClient = uuidv4()

      const newData = {
        ...data,
        assignedTest: testSelected ? testData : [],
        externalSelection: planExternal,
        internalSelection: planInternal,
        groupId: idForClient,
        id: idForClient,
        groupName: data.name,
        responsiblePhoneNumber: data.phone,
        responsibleEmail: data.email,
        // Pruebas de cortesía (opcional; 0 si se deja vacío)
        testsAvailable: Math.max(0, Math.trunc(Number(data.testsAvailable)) || 0),
        testsDone: 0
      };
      createClient(newData, () => {
        clientsList({ page: pageHandler.page, limit: pageHandler.limit });
        toast.success(`Cliente creado. Enviamos una invitación a ${newData.responsibleEmail} para que active su cuenta.`, {duration:6000});
        setData(initialDataStates);
        setPlanExternal(false);
        setPlanInternal(false);
        setTestSelected(false);
        setSteps(1)
        toggle();
      });
    } else {
      // `testsDone` no se edita aquí: es el contador de cortesía consumida y lo
      // preserva el backend. Enviamos el saldo de cortesía absoluto (issue #7).
      const editableData = { ...data };
      delete editableData.testsDone;
      const dataForEdit ={
        ...editableData,
        assignedTest: testSelected ? testData : [],
        externalSelection: planExternal,
        internalSelection: planInternal,
        responsiblePhoneNumber: data.phone,
        responsibleEmail: data.email,
        testsAvailable: Math.max(0, Math.trunc(Number(data.testsAvailable)) || 0),
      }
      editClient(dataForEdit, () => {
        toast.success("Cliente editado exitosamente", {duration:4000});
        toggle();
        setSteps(1)
    })

    }
  };

  useEffect(() => {
    const getListSector = async () => {
      const { data } = await UtilsAPI.getSectorList();
      if (data) {
        setSectorList(data);
      }
    };
    getListSector();
  }, []);

  const validRut = (rut) => {
    if (rut.length < 8) {
      return false;
    }
    return validateRut(data.rut);
  };

  const step2EditValid = data.responsibleUserId && data.responsiblePosition !== "";

  return (
    <Modal isOpen={open} toggle={toggle}>
      <div className="gm-modal-header">
        <div className="gm-modal-title-container">
          <div className="gm-modal-title">
            {isCreate ? "Crear Cliente" : "Editar Cliente"}
          </div>
        </div>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={()=>{
            toggle();
            handleCancelForm();
          }}
        />
      </div>
      <section className="gm-modal-section-circle-steps">
          <div className="gm-modal-section-step">
          <button
                    onClick={()=>{setSteps(1)}}
          className={
            steps === 1 ? "gm-modal-circle-active" : "gm-modal-circles-section"
          }
        >1</button>
        <span>INFORMACIÓN</span>
          </div>
          <div className="gm-modal-section-step">
          <button
          onClick={()=>{setSteps(2)}}
          className={
            steps === 2 ? "gm-modal-circle-active" : "gm-modal-circles-section"
          }
        >2</button>
        <span>RESPONSABLE</span>
          </div>
          <div className="gm-modal-section-step">
          <button
                    onClick={()=>{setSteps(3)}}
          className={
            steps === 3 ? "gm-modal-circle-active" : "gm-modal-circles-section"
          }
        >3</button>
        <span>SERVICIOS</span>
          </div>


        <div className="gm-modal-line"></div>
      </section>
      <ModalBody>
        <form onSubmit={handleSubmit}>
          <div className="gm-modal-form-container">
            {steps === 1 && (
              <div className="gm-modal-form-section">
                <div className="gm-modal-form-group">
                  <div className="gm-modal-input-group">
                    <label htmlFor="rut">Rut Empresa</label>
                    <Input
                      type="text"
                      id="rut"
                      name="rut"
                      onChange={(e) =>
                        handleChange("rut", cleanRut(e.target.value))
                      }
                      value={data?.rut ? formatRut(data.rut) : ""}
                      invalid={data?.rut ? !validRut(data.rut) : false}
                      maxLength={13}
                      required
                    />
                  </div>
                  <div className="gm-modal-form-group gm-wide-group">
                    <div className="gm-modal-input-group ">
                      <label htmlFor="sector">Rubro Empresa</label>
                      <select
                        required
                        onChange={(event) => {
                          handleChange("sector", event.target.value);
                        }}
                        value={data?.sector}
                      >
                        <option name="sector" value={null} id="sector">
                          Seleccione un rubro
                        </option>
                        {sectorList?.map((elements, index) => (
                          <option key={`${elements}-${index}`} name="sector" value={elements} id={`sector-${index}`}>
                            {elements}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="gm-modal-input-group">
                    <label htmlFor="name">Nombre Empresa</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={data.name}
                      onChange={(event) => {
                        handleChange("name", event.target.value);
                        handleValidateNotRepeated('name', event.target.value);
                      }}
                      disabled={!isCreate}
                    />
                    {isCreate && data.name.length >=1 && !nameRepeated && (
                    <span className="gm-span-modal-input">{TEXT_WARNING}</span>)}
                    {nameRepeated && <span className="gm-span-modal-input-error">{TEXT_ERROR_EMPRESA}</span>}
                  </div>
                </div>

                <div className="gm-modal-buttons">
                  <button
                    type="button"
                    className="gm-secondary-button"
                    onClick={() => {
                      setSteps(1);
                      handleCancelForm();
                      toggle();
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    className="gm-primary-button"
                    type="submit"
                    buttonLabel="Continuar"
                    disabled={
                      !validRut(data.rut) ||
                      data.sector === "" ||
                      data.name === "" ||
                      nameRepeated
                    }
                    loading={client.loading}
                    onClick={() => {
                      setSteps(steps + 1);
                    }}
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}
            {steps === 2 && (
              <div className="gm-modal-form-section">
                {!isCreate ? (
                  <div className="gm-modal-form-group gm-wide-group">
                    <div className="gm-modal-input-group">
                      <label htmlFor="responsibleUserId">Usuario Responsable</label>
                      <select
                        id="responsibleUserId"
                        value={data.responsibleUserId || ""}
                        onChange={(e) => handleSelectUser(e.target.value)}
                        disabled={loadingUsers}
                        required
                      >
                        <option value="">
                          {loadingUsers ? "Cargando usuarios..." : "Seleccionar usuario responsable"}
                        </option>
                        {clientUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.userEmail})
                          </option>
                        ))}
                      </select>
                    </div>
                    {data.responsibleUserId && (
                      <>
                        <div className="gm-modal-input-group">
                          <label>Nombre</label>
                          <input type="text" value={data.responsibleName} disabled />
                        </div>
                        <div className="gm-modal-input-group">
                          <label>Correo</label>
                          <input type="email" value={data.email} disabled />
                        </div>
                        <div className="gm-modal-input-group">
                          <label>Teléfono</label>
                          <input type="text" value={data.phone || "—"} disabled />
                        </div>
                      </>
                    )}
                    <div className="gm-modal-input-group">
                      <label htmlFor="responsiblePosition">Cargo</label>
                      <input
                        type="text"
                        id="responsiblePosition"
                        name="responsiblePosition"
                        required
                        value={data.responsiblePosition}
                        onChange={(event) => handleChange("responsiblePosition", event.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="gm-modal-form-group gm-wide-group">
                      <div className="gm-modal-input-group">
                        <label htmlFor="responsibleName">Nombre Responsable</label>
                        <input
                          type="text"
                          id="responsibleName"
                          name="responsibleName"
                          required
                          value={data.responsibleName}
                          onChange={(event) => {
                            handleChange("responsibleName", event.target.value);
                          }}
                        />
                      </div>
                    </div>
                    <div className="gm-modal-form-group gm-wide-group">
                      <div className="gm-modal-input-group">
                        <label htmlFor="responsiblePosition">Cargo</label>
                        <input
                          value={data.responsiblePosition}
                          type="text"
                          id="responsiblePosition"
                          name="responsiblePosition"
                          required
                          onChange={(event) => {
                            handleChange("responsiblePosition", event.target.value);
                          }}
                        />
                      </div>
                    </div>
                    <div className="gm-modal-form-group">
                      <div className="gm-modal-input-group">
                        <label htmlFor="responsibleEmail">Correo</label>
                        <Input
                          type="email"
                          id="responsibleEmail"
                          name="responsibleEmail"
                          value={data.email}
                          onChange={(e) =>{
                            handleValidateNotRepeated('email', e.target.value);
                            handleChange("email", e.target.value)
                          }}
                          maxLength={90}
                          invalid={validateEmail(data.email)}
                          required
                        />
                        {emailRepeated && <span className="gm-span-modal-input-error">{TEXT_ERROR_EMAIL}</span>}
                      </div>
                      <div className="gm-modal-input-group">
                        <label htmlFor="responsiblePhoneNumber">Teléfono</label>
                            <Input
                            type="text"
                            id="responsiblePhoneNumber"
                            name="responsiblePhoneNumber"
                            onChange={(e) => handleChange("phone", e.target.value)}
                            maxLength={20}
                            invalid={!newValidatePhone(data.phone)}
                            value={data.phone}
                            placeholder="+56 9 1234 5678"
                          />
                      </div>
                    </div>
                  </>
                )}
                <div className="gm-modal-buttons">
                  <button
                    type="button"
                    className="gm-secondary-button"
                    onClick={() => {
                      setSteps(1);
                      handleCancelForm();
                      toggle();
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    className="gm-primary-button"
                    type="submit"
                    buttonLabel="Continuar"
                    disabled={
                      isCreate
                        ? (data.responsibleName === "" ||
                           data.responsiblePosition === "" ||
                           data.email === "" ||
                           data.phone === "" ||
                           !newValidatePhone(data.phone) ||
                           emailRepeated)
                        : !step2EditValid
                    }
                    loading={client.loading}
                    onClick={() => {
                      setSteps(steps + 1);
                    }}
                  >
                    Continuar
                  </button>
                </div>
              </div>
            )}
            {steps === 3 && (
              <div className="gm-modal-form-section">
                <div className="gm-modal-form-group gm-wide-group">
                  <div className="gm-modal-input-group">
                    { featureToggles.isEnabled('enableClientsPlans') && (
                    <>
                    <div className="gm-modal-form-section-title">
                      Servicio Contratado
                    </div>
                    <div className="gm-modal-checkbox-section">
                      <label>Plan Selección Externa</label>
                      <input
                        className="input-checkbox"
                        type="checkbox"
                        onChange={() => {
                          setPlanExternal(!planExternal);
                        }}
                        checked={planExternal}
                      />
                      <label>Plan Desarrollo de Carrera</label>
                      <input
                        className="input-checkbox"
                        type="checkbox"
                        onChange={() => {
                          setPlanInternal(!planInternal);
                        }}
                        checked={planInternal}
                      />
                    </div>
                    </> )}
                  </div>
                  <div className="gm-modal-input-group">
                    <div className="gm-modal-form-section-title">
                      Test Asignado
                    </div>
                    <div className="gm-modal-checkbox-section">
                      {testData.map((test, index) => (
                        <>
                          <label key={`label-${index}`}>{test}</label>
                          <input
                            key={`input-${index}`}
                            className="input-checkbox"
                            name="assignedTest"
                            id={`assignedTest-${index}`}
                            type="checkbox"
                            onChange={() => {
                              setTestSelected(!testSelected);
                            }}
                            checked={testSelected}
                          />
                        </>
                      ))}
                      <label>Pruebas de cortesía a asignar (sin compra)</label>
                      <input
                        type="number"
                        id="testsAvailable"
                        name="testsAvailable"
                        min={0}
                        value={data?.testsAvailable}
                        onChange={(event) => {
                          const raw = event.target.value;
                          if (raw === "") return handleChange("testsAvailable", "");
                          const parsed = parseInt(raw, 10);
                          handleChange(
                            "testsAvailable",
                            Number.isNaN(parsed) ? "" : Math.max(0, parsed)
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="gm-modal-buttons">
                  <button
                    type="button"
                    className="gm-secondary-button"
                    onClick={() => {
                      setSteps(1);
                      handleCancelForm();
                      toggle();
                    }}
                  >
                    Cancelar
                  </button>
                  <ButtonLoading
                    className="gm-primary-button"
                    type="submit"
                    buttonLabel={isCreate ? "Crear" : "Guardar"}
                    disabled={
                      client.loading ||
                      (!planExternal && !planInternal && featureToggles.isEnabled('enableClientsPlans')) ||
                      (isCreate
                        ? (data.responsibleName === "" ||
                           data.responsiblePosition === "" ||
                           data.email === "" ||
                           data.phone === "" ||
                           !newValidatePhone(data.phone) ||
                           nameRepeated ||
                           emailRepeated)
                        : !step2EditValid) ||
                      !validRut(data.rut) ||
                      data.sector === "" ||
                      data.name === ""
                    }
                    loading={client.loading}
                  />
                </div>
              </div>
            )}
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default CreateClientModal;
